/*
 * HVACloud AI · Einheiten ergänzen — Task modal.
 *
 * Für jedes Leaf ohne Einheit unter dem gewählten Device fragt HVACloud den
 * Vorschlag für eine passende Einheit an (°C, bar, kW, kWh, rpm, %, m3/h,
 * l/min, V, A, Pa, mbar, Hz, K, h). Operator sieht Vorschlag mit Confidence-
 * Badge, kann pro Zeile deselektieren oder die Einheit editieren, dann Apply.
 */

import { useEffect, useState } from 'preact/hooks';
import { AiTaskChrome, type AiTaskStage } from './AiTaskChrome';
import { api, ApiError } from '../../api/client';
import { useAsync } from '../../hooks/useAsync';
import type { PlantSchemaResponse } from '../../api/types';
import { AiUnits } from '../../icons/HvacIcons';
import { BRAND } from '../../brand/brand';

interface Props {
  plantId: string;
  onClose(): void;
}

interface UnitItem {
  leafId: string;
  symbol: string;
  currentUnit: string | null;
  proposedUnit: string;
  confidence: 'high' | 'medium' | 'low';
}
interface UnitsProposal {
  deviceId: string;
  items: UnitItem[];
  rationale: string | null;
}

const PROGRESS_MESSAGES = [
  `${BRAND.aiName} liest Symbolnamen und Werttypen…`,
  'Physikalische Größen werden abgeleitet…',
  'Passende Einheiten werden vorgeschlagen…',
  'Fast fertig — Vorschlag wird validiert…',
];

export function AiUnitsModal({ plantId, onClose }: Props) {
  const [hint, setHint] = useState('');
  const [stage, setStage] = useState<AiTaskStage>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [proposal, setProposal] = useState<UnitsProposal | null>(null);
  const [deviceId, setDeviceId] = useState<string>('');
  // Per-row selection + edited value overrides. Keyed by leafId.
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [edited, setEdited] = useState<Record<string, string>>({});

  const plantState = useAsync(
    (signal) => api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal),
    [plantId],
  );
  const devices = plantState.status === 'ok' ? plantState.data.devices : [];

  useEffect(() => {
    if (deviceId === '' && devices.length > 0) setDeviceId(devices[0].id);
  }, [devices, deviceId]);

  const run = async () => {
    if (!deviceId) return;
    setStage('loading');
    setErrorMsg(null);
    try {
      const res = await api.post<UnitsProposal>(
        `/api/discovery/devices/${deviceId}/suggest-units`,
        { hint: hint.trim() || null });
      // Default-select every proposal; operator opt-outs are the cheap edit.
      const nextSel: Record<string, boolean> = {};
      const nextEd: Record<string, string> = {};
      for (const it of res.items) {
        nextSel[it.leafId] = it.confidence !== 'low';
        nextEd[it.leafId] = it.proposedUnit;
      }
      setProposal(res);
      setSelected(nextSel);
      setEdited(nextEd);
      setStage('result');
    } catch (e) {
      setErrorMsg(explain(e));
      setStage('error');
    }
  };

  const apply = async () => {
    if (!proposal) return;
    const updates = proposal.items
      .filter((it) => selected[it.leafId])
      .map((it) => ({
        leafId: it.leafId,
        unit: (edited[it.leafId] ?? it.proposedUnit).trim(),
      }))
      .filter((u) => u.unit !== '');
    if (updates.length === 0) { onClose(); return; }

    setStage('applying');
    setErrorMsg(null);
    try {
      await api.post<{ updatedLeaves: number }>(
        `/api/discovery/devices/${proposal.deviceId}/apply-units`,
        { updates });
      onClose();
    } catch (e) {
      setErrorMsg(explain(e));
      setStage('result');
    }
  };

  const selectedCount = proposal
    ? proposal.items.filter((it) => selected[it.leafId]).length
    : 0;

  return (
    <AiTaskChrome
      title={`${BRAND.aiName} · Einheiten ergänzen`}
      hint={hint}
      onHintChange={setHint}
      hintPlaceholder="z.B.: 'Steigungswerte sind K, nicht °C', 'Register 5000+ sind Zähler in kWh'"
      stage={stage}
      errorMessage={errorMsg}
      progressMessages={PROGRESS_MESSAGES}
      onClose={onClose}
      hero={<AiUnits size={72} style={{ color: 'var(--ai-accent)' }} />}
      chatContext={{ mode: 'units', plantId, deviceId: deviceId || undefined }}
      footer={
        stage === 'result' ? (
          <>
            <span class="muted" style={{ marginRight: 'auto' }}>
              {selectedCount} von {proposal?.items.length ?? 0} Einheiten werden übernommen
            </span>
            <button type="button" class="hv-btn" onClick={onClose}>Abbrechen</button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={apply}
                    disabled={selectedCount === 0}>
              Übernehmen
            </button>
          </>
        ) : stage === 'applying' ? (
          <button type="button" class="hv-btn" disabled>Übernehme…</button>
        ) : (
          <>
            <div style={{ flex: 1 }} />
            <button type="button" class="hv-btn" onClick={onClose} disabled={stage === 'loading'}>
              Abbrechen
            </button>
            <button type="button" class="hv-btn hv-btn--primary" onClick={run}
                    disabled={!deviceId || stage === 'loading' || plantState.status !== 'ok'}>
              {stage === 'loading' ? `${BRAND.aiName} arbeitet…` : 'Vorschlagen'}
            </button>
          </>
        )
      }
    >
      {stage === 'result' && proposal && (
        <>
          {proposal.rationale && <p class="hv-ai-modal__rationale">{proposal.rationale}</p>}
          {proposal.items.length === 0 ? (
            <p class="muted">Keine Einheiten-Vorschläge — {BRAND.aiName} hatte für keinen Datenpunkt eine ausreichend sichere Ableitung.</p>
          ) : (
            <table class="hv-data-table">
              <thead>
                <tr>
                  <th style={{ width: '2em' }}></th>
                  <th>Symbol</th>
                  <th style={{ width: '8em' }}>Einheit</th>
                  <th style={{ width: '6em', textAlign: 'center' }}>Vertrauen</th>
                </tr>
              </thead>
              <tbody>
                {proposal.items.map((it) => (
                  <tr key={it.leafId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selected[it.leafId]}
                        onChange={(e) => setSelected((s) => ({ ...s, [it.leafId]: (e.target as HTMLInputElement).checked }))}
                      />
                    </td>
                    <td class="mono">{it.symbol}</td>
                    <td>
                      <input
                        type="text"
                        value={edited[it.leafId] ?? it.proposedUnit}
                        onInput={(e) => setEdited((v) => ({ ...v, [it.leafId]: (e.target as HTMLInputElement).value }))}
                        style={{ width: '100%' }}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span class={`hv-ai-confidence hv-ai-confidence--${it.confidence}`}>
                        {it.confidence}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {(stage === 'idle' || stage === 'error') && (
        <div class="hv-modal__field">
          <label>Device</label>
          <select value={deviceId}
                  onChange={(e) => setDeviceId((e.target as HTMLSelectElement).value)}
                  disabled={devices.length === 0}>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>{d.name} ({d.symbol})</option>
            ))}
            {devices.length === 0 && <option value="">— keine Devices —</option>}
          </select>
        </div>
      )}
    </AiTaskChrome>
  );
}

function explain(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; detail?: string; title?: string } | undefined;
    return body?.error ?? body?.detail ?? body?.title ?? `Fehler ${e.status}`;
  }
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler.';
}
